import { useCallback } from 'react'
import type { Map } from 'mapbox-gl'
import { useAtom } from 'jotai'
import type { DrawArea, MapLayer } from '@app/types/layerTypes'
import type { RegionOption } from '@app/components/Molecules/RegionFilter'
import type { IdentifyingInformationFilter } from '@app/types/filterTypes'
import {
  getVisibleAssetIds,
  getVisibleInformationIds,
} from '@app/components/Molecules/MapView/RiskMapView/RiskMapView.utilities'
import { visibleAssetsAtom } from '@app/risk-migration/atoms/riskLayers.atoms'

interface CalculateVisibleAssetsProps {
  map: Map
  assetLayers: MapLayer[]
  contextualLayers: MapLayer[]
  drawAreas: DrawArea[]
  regionMasks: RegionOption[] | null
  hasFilters: boolean
  elementFilters: IdentifyingInformationFilter
}

export function useVisibleAssets() {
  const [visibleAssets, setVisibleAssets] = useAtom(visibleAssetsAtom)

  const calculateVisibleAssets = useCallback(
    async ({
      map,
      assetLayers,
      contextualLayers,
      drawAreas,
      regionMasks,
      hasFilters,
      elementFilters,
    }: CalculateVisibleAssetsProps) => {
      if (!hasFilters) {
        setVisibleAssets({})
        return {}
      }

      const [assetIds, informationIds] = await Promise.all([
        getVisibleAssetIds(assetLayers, drawAreas, regionMasks, elementFilters),
        getVisibleInformationIds(contextualLayers, map, drawAreas),
      ])

      const result = {
        ...assetIds,
        ...informationIds,
      }

      setVisibleAssets(result)

      return result
    },
    [setVisibleAssets],
  )

  return {
    visibleAssets,
    calculateVisibleAssets,
  }
}
